import { Assignment } from './Assignment';
import { ORGANIZATIONS_COLLECTION } from './Organization';
import { PROJECTS_COLLECTION } from './Project';

export const CHECKLIST_COLLECTION = 'checklists';
export const CHECKLIST_TYPE_COLLECTION = 'checklist-types';
export const CHECKLIST_TYPE_ITEM = 'types';

export const CHECKLIST_FOLDERS = 'checklist-folders';
export const CHECKLIST_ITEMS_COLLECTION = 'items';
export const CHECKLIST_NOTES_COLLECTION = 'notes';
export const CHECKLIST_ACTIVITY_LOG_COLLECTION = 'logs';

export const CHECKLIST_COVER_IMAGE = 'cover-image';

export const getChecklistTypesDocPath = (organizationId: string) => {
	return `${ORGANIZATIONS_COLLECTION}/${organizationId}/${CHECKLIST_TYPE_COLLECTION}/${CHECKLIST_TYPE_ITEM}`;
};

export const getChecklistFoldersDocPath = (projectId: string) => {
	return `${PROJECTS_COLLECTION}/${projectId}/${CHECKLIST_FOLDERS}/${CHECKLIST_FOLDERS}`;
};

export const getChecklistImageLocation = (
	projectId: string,
	checklistId: string,
) =>
	`${PROJECTS_COLLECTION}/${projectId}/${CHECKLIST_COLLECTION}/${checklistId}/${CHECKLIST_COVER_IMAGE}`;

export const getProjectChecklistCollection = (projectId: string) =>
	`${PROJECTS_COLLECTION}/${projectId}/${CHECKLIST_COLLECTION}`;

export const getProjectChecklistActivityLogCollection = (
	projectId: string,
	checklistId: string,
) =>
	`${PROJECTS_COLLECTION}/${projectId}/${CHECKLIST_COLLECTION}/${checklistId}/${CHECKLIST_ACTIVITY_LOG_COLLECTION}`;

// projects/:projectId/checklists/:checklistId/:checklistItemId
export const getProjectChecklistNotesCollection = (
	projectId: string,
	checklistId: string,
	itemId: string,
) =>
	`${PROJECTS_COLLECTION}/${projectId}/${CHECKLIST_COLLECTION}/${checklistId}/${CHECKLIST_ITEMS_COLLECTION}/${itemId}/${CHECKLIST_NOTES_COLLECTION}`;

export const getProjectChecklistMainNotesCollection = (
	projectId: string,
	checklistId: string,
) =>
	`${PROJECTS_COLLECTION}/${projectId}/${CHECKLIST_COLLECTION}/${checklistId}/${CHECKLIST_NOTES_COLLECTION}`;

/**
 * Returns the collection reference for an organizations project checklist templates.
 * @param organizationId to look up the templates
 * @returns the string of the org's projects checklist templates.
 */
export const getChecklistTemplateCollection = (organizationId: string) =>
	`${ORGANIZATIONS_COLLECTION}/${organizationId}/${CHECKLIST_COLLECTION}`;

// projects/:projectId/checklists/:checklistId
export interface Checklist {
	id: string;
	name: string;
	description?: string;
	created: Date;
	updated: Date;
	status: ChecklistStatus;
	locked: boolean;
	type?: string;
	sections: ChecklistSection[];
	lastUserId: string;
	defaultChecklist?: boolean;
	folderPath: string | null;
}

export interface ChecklistTypes {
	types: string[];
}

export enum ChecklistStatus {
	DRAFT = 'DRAFT',
	IN_PROGRESS = 'IN_PROGRESS',
	PENDING_APPROVED = 'PENDING_APPROVED',
	COMPLETE = 'COMPLETE',
}

export interface ChecklistSection {
	name: string;
	items: ChecklistItem[];
}

export interface PrintChecklistProps {
	checklist: Checklist;
	assignment?: Assignment;
	projectName: string;
	projectId: string;
	organizationName: string;
	organizationId: string;
}

export interface ChecklistItem {
	id?: string;
	description: string;
	status: ChecklistItemStatus;
}

export enum ChecklistItemStatus {
	NOT_APPLICABLE = 'NOT_APPLICABLE',
	INCOMPLETE = 'INCOMPLETE',
	COMPLETE = 'COMPLETE',
}

export interface ChecklistTemplateExtra {
	createAssignment?: boolean;
	assignedDuty?: string[];
	approverDuty?: string[];
	notifyDuty?: string[];
}

// organizations/:orgId/checklists/:checkListId
export type ChecklistTemplate = Omit<
	Checklist,
	'status' | 'locked' | 'created' | 'updated'
> &
	ChecklistTemplateExtra;

export function getChecklistCompleted(checklists: Checklist) {
	let totalAmount = 0;
	let completedAmount = 0;

	checklists.sections.forEach(section =>
		section.items.forEach(item => {
			totalAmount++;
			if (
				item.status === ChecklistItemStatus.COMPLETE ||
				item.status === ChecklistItemStatus.NOT_APPLICABLE
			) {
				completedAmount++;
			}
		}),
	);
	return {
		completedAmount,
		totalAmount,
		isCompleted:
			completedAmount === totalAmount &&
			checklists.status === ChecklistStatus.COMPLETE,
	};
}

export interface ChecklistChildren {
	[key: string]: ChecklistFolder;
}

export interface ChecklistFolder {
	id: string;
	name: string;
	description?: string;
	children: ChecklistChildren;
}

export interface ChecklistFolders {
	folders: ChecklistChildren;
}
